import Analytics from "analytics";
import googleAnalyticsPlugin from "@analytics/google-analytics";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getProductMeta } from "./prices";


// Initialize analytics and plugins
// Documentation: https://getanalytics.io
const analytics = Analytics({
  debug: process.env.NODE_ENV !== "production",
  plugins: [
    googleAnalyticsPlugin({
      measurementIds: [process.env.REACT_APP_GA_MEASUREMENT_ID],
    }),
  ],
});

// Track initial pageview
if (typeof window !== "undefined") {
  analytics.page();
}

// Component to track route changes
// Must be placed inside Router component in _app.js
export function RouteChangeTracker() {
  const location = useLocation();

  useEffect(() => {
    analytics.page();
  }, [location.pathname, location.search]);

  return null;
}

// T-011: Helper function for InitiateCheckout event
export function trackInitiateCheckout(productInfo) {
  try {
    // Facebook Pixel
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: productInfo.name,
        content_ids: [productInfo.id],
        content_type: 'product',
        value: productInfo.value,
        currency: 'USD'
      });
    }

    // Google Analytics
    analytics.track('initiate_checkout', {
      items: [{
        item_id: productInfo.id,
        item_name: productInfo.name,
        price: productInfo.value
      }],
      value: productInfo.value,
      currency: 'USD'
    });
  } catch (error) {
    console.error('InitiateCheckout tracking error:', error);
  }
}

/**
 * Track a Purchase event on Meta pixel and GA.
 * sessionStorage dedup prevents double-fires on re-render, refresh, or React 18 StrictMode.
 * @param {string} productId - Product key from productCatalog (e.g. 'sbm', 'tp', 'proc')
 */
export function trackPurchase(productId) {
  try {
    const dedupKey = `purchase_tracked_${productId}`;
    if (sessionStorage.getItem(dedupKey)) {
      console.log(`[analytics] Purchase already tracked for ${productId}, skipping`);
      return;
    }

    const meta = getProductMeta(productId);
    const contentName = meta ? meta.name : productId;
    const value = meta ? meta.value : 0;
    const productLine = meta ? meta.product_line : 'unknown';

    if (!meta) {
      console.warn(`[analytics] Unknown product "${productId}" — firing generic Purchase with value 0`);
    }

    // Meta Pixel
    if (window.fbq) {
      window.fbq('track', 'Purchase', {
        content_ids: [productId],
        content_name: contentName,
        content_type: 'product',
        value: value,
        currency: 'USD',
        content_category: productLine,
      });
    }

    // Google Analytics
    analytics.track('purchase', {
      items: [{
        item_id: productId,
        item_name: contentName,
        price: value,
      }],
      value: value,
      currency: 'USD',
    });

    sessionStorage.setItem(dedupKey, 'true');
    console.log(`[analytics] Purchase tracked: ${productId} (${contentName}) $${value}`);
  } catch (error) {
    console.error('[analytics] Purchase tracking error:', error);
  }
}

export default analytics;
